import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import BaseLayout from "@/v2/components/BaseLayout";
import { fetchTrendarItems, type TrendItem } from "@/v2/queries";
import { decodeEntities } from "@/v2/textUtils";

const SITE = "https://korthet.se";

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const diffMin = Math.max(1, Math.round((Date.now() - d.getTime()) / 60000));
    if (diffMin < 60) return `${diffMin} min sedan`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH} tim sedan`;
    return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

function hostOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function TrendRow({ item }: { item: TrendItem }) {
  const host = hostOf(item.sourceUrl);
  const favicon = host
    ? `https://www.google.com/s2/favicons?domain=${host}&sz=32`
    : null;
  const label = item.source || host || "Källa";
  const internal = item.slug ? `/trendar/${item.slug}` : null;
  return (
    <li className="k2-card">
      <h2 className="k2-card-title">
        {internal ? (
          <Link to={internal}>{decodeEntities(item.title)}</Link>
        ) : item.sourceUrl ? (
          <a href={item.sourceUrl} rel="nofollow noopener external" target="_blank">
            {decodeEntities(item.title)}
          </a>
        ) : (
          decodeEntities(item.title)
        )}
      </h2>
      <div className="k2-meta">
        <span className="k2-cat-badge" data-cat="trendar">Trendar</span>
        <span className="k2-source">
          {favicon && (
            <img
              src={favicon}
              alt=""
              width={16}
              height={16}
              className="k2-favicon"
              loading="lazy"
            />
          )}
          {label}
        </span>
        {item.publishedAt && (
          <time dateTime={item.publishedAt}>{formatTime(item.publishedAt)}</time>
        )}
      </div>
    </li>
  );
}

export default function TrendarPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["v2", "trendar"],
    queryFn: () => fetchTrendarItems(60),
  });

  const canonicalUrl = `${SITE}/trendar`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Korthet",
      url: `${SITE}/`,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Korthet",
      url: `${SITE}/`,
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Trendar – Korthet",
      url: canonicalUrl,
      inLanguage: "sv-SE",
      isPartOf: { "@type": "WebSite", name: "Korthet", url: `${SITE}/` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Trendar", item: canonicalUrl },
      ],
    },
  ];

  return (
    <BaseLayout
      title="Trendar – Vad söker Sverige just nu | Korthet"
      description="Trendande nyheter från svenska källor just nu. Uppdateras löpande."
      canonicalPath="/trendar"
      jsonLd={jsonLd}
    >
      <h1 className="k2-h1">Trendar</h1>
      <p className="k2-lede">Trendande ämnen från svenska nyhetskällor.</p>
      {isLoading && <p className="k2-loading">Laddar…</p>}
      {error && <p className="k2-error">Kunde inte hämta trendar.</p>}
      {data && data.length === 0 && (
        <p className="k2-empty">Inga svenska trendar just nu.</p>
      )}
      {data && data.length > 0 && (
        <ul className="k2-feed">
          {data.map((item) => (
            <TrendRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </BaseLayout>
  );
}
